import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import { alpha, styled } from "@mui/material/styles";

import { MASCOT_PALETTE } from "@/components/Others/MascotPanel";
import { colors } from "@/theme";
import { cloudPatternUrl } from "@/theme/customizations";

const STILL = "@media (prefers-reduced-motion: reduce)";

/**
 * The foot of every page.
 *
 * A surface of its own - the card color of each scheme, under the same cloud
 * watermark as the page - so the links read as a block that closes the page
 * rather than as more content between two rules.
 */
export const FooterRoot = styled("footer")(({ theme }) => ({
  position: "relative",
  // The theme's 4px Container gutter leaves the footer flush with the screen edge.
  "& > .MuiContainer-root": {
    paddingLeft: theme.spacing(2),
    paddingRight: theme.spacing(2),
    [theme.breakpoints.up("md")]: {
      paddingLeft: theme.spacing(4),
      paddingRight: theme.spacing(4),
    },
  },
  borderTop: `1px solid ${colors.border}`,
  backgroundColor: colors.white,
  backgroundImage: cloudPatternUrl(colors.prime),
  ...theme.applyStyles("dark", {
    borderTop: `1px solid ${colors.borderDark}`,
    backgroundColor: colors.layer1Dark,
  }),
}));

/** The mascot seeing the player off: porthole, a line from him, the way to his page. */
export const GreeterRoot = styled(Box)(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  gap: theme.spacing(3),
  // Inset so the mascot's halo clears the edge.
  padding: theme.spacing(4, 3),
  borderBottom: `1px solid ${colors.border}`,
  ...theme.applyStyles("dark", { borderBottom: `1px solid ${colors.borderDark}` }),
  [theme.breakpoints.down("md")]: {
    flexDirection: "column",
    textAlign: "center",
    gap: theme.spacing(2),
    padding: theme.spacing(3, 2),
  },
}));

/**
 * The porthole, afloat on a pool of brand light.
 *
 * The halo is what keeps a navy disc from sitting on the footer like a sticker,
 * in the light scheme most of all.
 */
export const GreeterMascot = styled(Box)(({ theme }) => ({
  position: "relative",
  flexShrink: 0,
  animation: "footer-mascot-float 7s ease-in-out infinite",
  "@keyframes footer-mascot-float": {
    "0%, 100%": { transform: "translate3d(0, -3px, 0)" },
    "50%": { transform: "translate3d(0, 5px, 0)" },
  },
  "&::before": {
    content: '""',
    position: "absolute",
    inset: "-28%",
    borderRadius: "50%",
    background: `radial-gradient(circle, ${alpha(colors.prime, 0.2)} 0%, transparent 68%)`,
    pointerEvents: "none",
  },
  ...theme.applyStyles("dark", {
    "&::before": {
      background: `radial-gradient(circle, ${alpha(colors.prime, 0.34)} 0%, transparent 68%)`,
    },
  }),
  [STILL]: { animation: "none" },
}));

export const GreeterEyebrow = styled(Typography)(({ theme }) => ({
  fontSize: 11,
  fontWeight: 700,
  textTransform: "uppercase",
  letterSpacing: 1,
  color: MASCOT_PALETTE.deepInk,
  ...theme.applyStyles("dark", { color: MASCOT_PALETTE.sky }),
})) as typeof Typography;

/** The brand on the left, the link columns beside it; one under the other on a phone. */
export const FooterMain = styled(Box)(({ theme }) => ({
  display: "grid",
  gridTemplateColumns: "minmax(260px, 1fr) 2fr",
  gap: theme.spacing(6),
  padding: theme.spacing(5, 0),
  [theme.breakpoints.down("md")]: {
    gridTemplateColumns: "1fr",
    gap: theme.spacing(4),
    padding: theme.spacing(4, 0),
  },
}));

/**
 * The link columns.
 *
 * Two to a row on a phone, where they used to be hidden altogether - the terms
 * and the help pages could not be reached from one.
 */
export const LinkGrid = styled("nav")(({ theme }) => ({
  display: "grid",
  gridTemplateColumns: "repeat(auto-fit, minmax(140px, 1fr))",
  alignItems: "start",
  gap: theme.spacing(4, 3),
  [theme.breakpoints.down("sm")]: {
    gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
    gap: theme.spacing(3, 2),
  },
}));

export const SectionContainer = styled(Box)(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  alignItems: "flex-start",
  gap: theme.spacing(0.5),
  minWidth: 0,
}));

export const SectionTitle = styled(Typography)(({ theme }) => ({
  marginBottom: theme.spacing(0.75),
  fontSize: 12,
  fontWeight: 700,
  textTransform: "uppercase",
  letterSpacing: 0.8,
  color: colors.dark,
  ...theme.applyStyles("dark", { color: colors.white }),
})) as typeof Typography;

export const SectionLink = styled(Typography)(({ theme }) => ({
  maxWidth: "100%",
  padding: theme.spacing(0.5, 0),
  borderRadius: 4,
  overflowWrap: "anywhere",
  textDecorationLine: "none",
  color: alpha(colors.dark, 0.72),
  transition: theme.transitions.create("color", {
    duration: theme.transitions.duration.short,
  }),
  "&:hover": { color: colors.schnitzel },
  "&:focus-visible": { outline: `2px solid ${MASCOT_PALETTE.sky}`, outlineOffset: 2 },
  ...theme.applyStyles("dark", {
    color: colors.grey,
    "&:hover": { color: MASCOT_PALETTE.sky },
  }),
  [STILL]: { transition: "none" },
})) as typeof Typography;

/** The row of partner and payment marks the admin uploads. */
export const PartnerRow = styled(Box)(({ theme }) => ({
  display: "flex",
  flexWrap: "wrap",
  alignItems: "center",
  gap: theme.spacing(2, 3),
  padding: theme.spacing(3, 0),
  borderTop: `1px solid ${colors.border}`,
  ...theme.applyStyles("dark", { borderTop: `1px solid ${colors.borderDark}` }),
  [theme.breakpoints.down("md")]: { justifyContent: "center" },
  "& img": { height: 28, width: "auto", maxWidth: 120, objectFit: "contain" },
}));

/** Copyright and the age line on one side, the language on the other. */
export const FooterBar = styled(Box)(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  gap: theme.spacing(2),
  padding: theme.spacing(2, 0),
  borderTop: `1px solid ${colors.border}`,
  ...theme.applyStyles("dark", { borderTop: `1px solid ${colors.borderDark}` }),
  [theme.breakpoints.down("md")]: {
    flexDirection: "column",
    textAlign: "center",
  },
}));
